import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import AddLinkIcon from '@mui/icons-material/AddLink'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useStep } from '../hooks/useStep'
import { useToken } from '../hooks/useToken'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import {
  useBatchScrape,
  applyResults as applyUploadResults,
  type PageEntry,
} from '../hooks/useBatchScrape'
import { fetchActiveUpload, waitForUploadJob } from '@/api/docsAPI'
import { useQueryClient } from '@tanstack/react-query'
import { useDiscoverPages } from '../hooks/useDiscoverPages'
import { useOrgDocs } from '@/utils/hooks/orgs'
import { isBlockedUrl, getBlockedUrlMessage } from '../utils/urlValidation'
import { normalizeUrl } from '../utils/normalizeUrl'
import { COLORS } from '../theme'
import {
  stepHeadingSx,
  stepDescriptionSx,
  outlinedButtonSx,
  warningBoxSx,
  textFieldSx,
  inputSx,
} from '../styles'
import UploadProgressScreen, {
  type ProgressItem,
} from '../components/UploadProgressScreen'
import ProgressScreen from '../components/ProgressScreen'
import type { UUID } from 'crypto'
import type { DiscoveredPage } from '../types'

const MAX_URLS = 10
const DISCOVERY_PROGRESS_INCREMENT = 90 / 15 // 0→90% over ~15 seconds

/** Map backend error strings to user-friendly messages.
 *
 * Kept separate from `formatWebsiteUploadError` deliberately: that one appends
 * guidance to the raw backend string, which suits the profile page, while the
 * wizard shows fully curated copy per page. What this had drifted on was the
 * blocked-host case, which fell through to "could not be read" and had users
 * retrying a URL that will never work.
 */
function formatScrapeError(error?: string): string {
  if (!error)
    return 'We hit an unexpected problem reading this page — any missing info can be added in the next step'
  if (error.includes('Website URL not supported')) return getBlockedUrlMessage()
  // A URL the validator rejected outright — bad shape, private address. Distinct
  // from a blocked host: this one genuinely is not a usable address.
  if (error.includes('Website URL not allowed'))
    return 'This address could not be used — please check it is a valid, publicly reachable URL'
  if (error.includes('Failed to access website'))
    return 'Could not access this page — please check the URL is correct and publicly accessible'
  if (error.includes('Failed to extract'))
    return 'This website could not be read — any missing info can be added in the next step'
  if (error.includes('already been uploaded'))
    return 'This page has already been uploaded'
  // Honest about not knowing, rather than blaming the page itself.
  return 'We hit an unexpected problem reading this page — any missing info can be added in the next step'
}

export default function UploadWebpages() {
  const { orgUuid, maxStepReached, setStaleFrom, setHideNav } =
    useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()
  const { getToken } = useToken()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const batchScrape = useBatchScrape(orgUuid ?? '', getToken)
  const queryClient = useQueryClient()
  const { data: docs, deleteOrgDoc } = useOrgDocs(orgUuid as UUID, token)
  const discoverPages = useDiscoverPages()

  const pages = wizardData.pages

  // The adoption effect runs detached from render, so it needs the live page
  // list rather than the one captured when it started.
  const latestPagesRef = useRef(pages)
  latestPagesRef.current = pages
  // One adoption at a time; a re-render mid-flight must not start a second.
  const adoptingRef = useRef(false)
  // Set once the user has been told some pages were never submitted, so the
  // next Next press goes through. Deliberately never reset within a mount:
  // resetting per run would make a second partial run block Next again, which
  // is the trapping behaviour this acknowledgement exists to remove.
  const partialRunAcknowledgedRef = useRef(false)
  const [urlInput, setUrlInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showProgress, setShowProgress] = useState(false)
  const scrapingUrlsRef = useRef<string[]>([])
  const pagesChangedRef = useRef(false)
  const mountedRef = useRef(true)

  // ── Discovery state machine ─────────────────────────────────
  // init: waiting for DB docs to load, then check cache
  // discovering: running discovery API with blocking progress screen
  // ready: discovery done (or skipped), show URL editor
  const [phase, setPhase] = useState<'init' | 'discovering' | 'ready'>('init')
  const discoveryAbortedRef = useRef(false)

  // ── Fake progress for discovery screen (0→90% over ~15s) ──
  const [discoveryProgress, setDiscoveryProgress] = useState(0)
  const discoveryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  )

  useEffect(() => {
    if (phase !== 'discovering') return
    setDiscoveryProgress(0)
    discoveryIntervalRef.current = setInterval(() => {
      setDiscoveryProgress((prev) => {
        if (prev >= 90) {
          if (discoveryIntervalRef.current)
            clearInterval(discoveryIntervalRef.current)
          return prev
        }
        return prev + DISCOVERY_PROGRESS_INCREMENT
      })
    }, 1000)
    return () => {
      if (discoveryIntervalRef.current)
        clearInterval(discoveryIntervalRef.current)
    }
  }, [phase])

  const websiteDocs = useMemo(
    () => docs?.filter((d) => d.website_url) ?? [],
    [docs],
  )
  const docsLoaded = docs !== undefined
  const orgWebsite = wizardData.orgForm?.website?.trim() ?? ''

  // Merge discovered pages + DB website docs into wizardData.pages
  const mergePages = useCallback(
    (discovered: DiscoveredPage[]) => {
      const dbPages: PageEntry[] = websiteDocs.map((d) => ({
        url: d.website_url!,
        status: 'ingested' as const,
        docId: d.id,
        dateAdded: d.date_added
          ? new Date(d.date_added).toLocaleDateString('en-NZ', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : undefined,
      }))
      const dbUrls = new Set(dbPages.map((p) => p.url))

      const remainingSlots = Math.max(0, MAX_URLS - dbPages.length)
      let pendingPages: PageEntry[] = discovered
        .filter((p) => !dbUrls.has(p.url))
        // Filtered at merge rather than at fetch: discovered pages are persisted
        // in wizard_data and localStorage, so blocked entries cached before this
        // shipped would otherwise keep reappearing on every return visit.
        .filter((p) => !isBlockedUrl(p.url))
        .slice(0, remainingSlots)
        .map((p) => ({
          url: p.url,
          title: p.title,
          status: 'pending' as const,
        }))

      // If discovery found nothing, seed the main website URL so the user
      // sees at least their own site queued for scanning.
      if (pendingPages.length === 0 && remainingSlots > 0 && orgWebsite) {
        const url = orgWebsite.startsWith('http')
          ? orgWebsite
          : `https://${orgWebsite}`
        if (!dbUrls.has(url)) {
          pendingPages = [{ url, status: 'pending' as const }]
        }
      }

      const merged = [...dbPages, ...pendingPages]
      if (merged.length > 0) {
        setWizardData({ pages: merged })
      }
    },
    [websiteDocs, setWizardData, orgWebsite],
  )

  // Ref so async callbacks always call the latest mergePages
  const mergePagesRef = useRef(mergePages)
  mergePagesRef.current = mergePages

  // Re-attach to an upload this client stopped watching.
  //
  // The crawl runs on the worker and finishes whether or not anyone is
  // listening, so a dropped connection, a token refresh, a reload or a second
  // tab all leave a job running with nobody applying its result. Asking the
  // server what is in flight — rather than persisting a job id here — means a
  // job started on another device is adopted on the same footing as a local one.
  useEffect(() => {
    if (!orgUuid) return
    // Wait until nothing local is running, then look for work we lost track of.
    if (batchScrape.isRunning) return
    // Read from the ref rather than depending on `pages`: this must fire on the
    // falling edge of isRunning, not every time the page list changes, or
    // applying a result would re-trigger it and the effect would chase itself.
    if (!latestPagesRef.current.some((p) => p.status === 'processing')) return
    if (adoptingRef.current) return
    adoptingRef.current = true

    let cancelled = false
    const controller = new AbortController()

    const adopt = async () => {
      try {
        const token = await getToken()
        const active = await fetchActiveUpload({
          token,
          orgUuid,
          signal: controller.signal,
        })
        if (cancelled) return

        if (!active) {
          // Nothing running: the job finished while we were away. The docs query
          // below is what turns those pages green.
          queryClient.invalidateQueries({ queryKey: ['orgDocs', orgUuid] })
          return
        }

        const results = await waitForUploadJob({
          token,
          jobId: active.job_id,
          signal: controller.signal,
        })
        if (cancelled) return
        setWizardData({
          pages: applyUploadResults(latestPagesRef.current, results),
        })
        queryClient.invalidateQueries({ queryKey: ['orgDocs', orgUuid] })
      } catch {
        // Still unknown. Leave the pages as they are rather than inventing a
        // failure — the mount-time reconcile settles it on the next visit.
      } finally {
        adoptingRef.current = false
      }
    }

    void adopt()
    return () => {
      cancelled = true
      controller.abort()
    }
    // Fires on mount and whenever a local run finishes — the in-session case,
    // where the watch is lost mid-batch and nothing else would pick the job up
    // until the user left the step and came back.
  }, [orgUuid, batchScrape.isRunning])

  // Check cache freshness and decide whether to run discovery blocking.
  // Step 6 fires background discovery as a warm cache. If it completed before
  // we mount, we merge instantly (cache hit). If not, we run our own discovery
  // call with a blocking progress screen so the user can't add URLs while
  // discovery is in flight — eliminating the race condition.
  //
  // Guard: the effect only runs when phase === 'init'. Every branch below
  // calls setPhase(), so the phase transitions away from 'init' on the very
  // first successful run — no ref needed to prevent double-fire.
  useEffect(() => {
    if (phase !== 'init') return
    if (!docsLoaded) return

    // Return visit with pages already populated — reconcile against DB
    // so deletions made in profile routes are reflected.
    if (pages.length > 0) {
      const dbUrlSet = new Set(websiteDocs.map((d) => d.website_url))
      const dbIdSet = new Set(websiteDocs.map((d) => d.id))
      const dbDocByUrl = new Map(websiteDocs.map((d) => [d.website_url, d]))
      const reconciled = pages
        .filter((p) => {
          // Keep pending/processing pages (not yet in DB)
          if (p.status !== 'ingested') return true
          // Keep ingested pages that still exist in DB
          return p.docId ? dbIdSet.has(p.docId) : dbUrlSet.has(p.url)
        })
        .map((p) => {
          // The DB is the source of truth for what was ingested; a cached
          // 'error' is not, and neither is a stranded 'processing'. A page whose
          // doc exists was read successfully — the client just stopped watching
          // before the job finished. Left alone, an 'error' stayed red forever
          // (one org still saw failures for content ingested nine days earlier),
          // and a 'processing' fell through to the reset below, got resubmitted,
          // and came back as "already been uploaded" — red again, for a page
          // that is fine.
          if (p.status !== 'error' && p.status !== 'processing') return p
          const doc = dbDocByUrl.get(p.url)
          if (!doc) {
            // Nothing landed for this one. An error becomes a real retry rather
            // than a permanent failure the user cannot clear; a 'processing'
            // page is left for the stale reset below, which does the same.
            return p.status === 'error'
              ? { ...p, status: 'pending' as const, error: undefined }
              : p
          }
          return {
            ...p,
            status: 'ingested' as const,
            error: undefined,
            docId: doc.id,
            dateAdded: doc.date_added
              ? new Date(doc.date_added).toLocaleDateString('en-NZ', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : p.dateAdded,
          }
        })
        // Reset stale 'processing' pages — nothing this mount started is
        // running, so these are leftovers from an interrupted session
        // (exit/logout). A job still running server-side is picked up
        // separately by the adoption effect, which re-marks its pages.
        .map((p) =>
          p.status === 'processing' ? { ...p, status: 'pending' as const } : p,
        )
      const changed =
        reconciled.length !== pages.length ||
        reconciled.some((p, i) => p !== pages[i])
      if (changed) {
        setWizardData({ pages: reconciled })
      }
      setPhase('ready')
      return
    }

    // No website — merge DB docs only
    if (!orgWebsite || !orgUuid) {
      mergePages([])
      setPhase('ready')
      return
    }

    // Cache hit — step 6 pre-fetch already completed for this website
    if (
      normalizeUrl(wizardData.discoveredForWebsite) === normalizeUrl(orgWebsite)
    ) {
      mergePages(wizardData.discoveredPages)
      setPhase('ready')
      return
    }

    // Cache stale — run discovery blocking
    discoveryAbortedRef.current = false
    setPhase('discovering')
    setHideNav(true)

    const run = async () => {
      try {
        const freshToken = await getToken()
        const discovered = await discoverPages.mutateAsync({
          token: freshToken,
          orgUuid: orgUuid!,
          website: orgWebsite,
        })
        if (!discoveryAbortedRef.current) {
          setWizardData({
            discoveredPages: discovered,
            discoveredForWebsite: orgWebsite,
          })
          mergePagesRef.current(discovered)
        }
      } catch {
        if (!discoveryAbortedRef.current) {
          mergePagesRef.current([])
        }
      }
      if (!discoveryAbortedRef.current) {
        setPhase('ready')
        setHideNav(false)
      }
    }
    run()
  }, [phase, docsLoaded])

  // ── Domain splitting ────────────────────────────────────────
  const mainWebsite = wizardData.orgForm?.website ?? ''
  const orgHostname = useMemo(() => {
    try {
      const url = mainWebsite.startsWith('http')
        ? mainWebsite
        : `https://${mainWebsite}`
      return new URL(url).hostname.toLowerCase().replace(/^www\./, '')
    } catch {
      return ''
    }
  }, [mainWebsite])

  const isOwnDomain = useCallback(
    (url: string): boolean => {
      if (!orgHostname) return true
      try {
        const normalised = url.startsWith('http') ? url : `https://${url}`
        const hostname = new URL(normalised).hostname
          .toLowerCase()
          .replace(/^www\./, '')
        return hostname === orgHostname || hostname.endsWith(`.${orgHostname}`)
      } catch {
        return false
      }
    },
    [orgHostname],
  )

  const ownPages = pages.filter((p) => isOwnDomain(p.url))
  const externalPages = pages.filter((p) => !isOwnDomain(p.url))

  const handleAddUrl = useCallback(() => {
    const trimmed = urlInput.trim()
    if (!trimmed) return

    if (pages.length >= MAX_URLS) {
      setError(`Maximum ${MAX_URLS} URLs allowed`)
      return
    }

    try {
      new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`)
    } catch {
      setError('Please enter a valid URL')
      return
    }

    if (isBlockedUrl(trimmed)) {
      setError(getBlockedUrlMessage())
      return
    }

    const url = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`

    if (pages.some((p) => p.url === url)) {
      setError('This URL is already in the list')
      return
    }

    setError(null)
    setUrlInput('')
    pagesChangedRef.current = true
    setWizardData((prev) => ({
      pages: [...prev.pages, { url, status: 'pending' as const }],
    }))
  }, [urlInput, pages, setWizardData])

  const handleRemove = useCallback(
    async (url: string) => {
      const page = pages.find((p) => p.url === url)
      if (!page || page.status === 'processing') return

      if (page.status === 'ingested' && page.docId) {
        try {
          await deleteOrgDoc.mutateAsync({ token, docId: page.docId })
        } catch {
          // Best-effort delete
        }
      }

      pagesChangedRef.current = true
      setWizardData((prev) => ({
        pages: prev.pages.filter((p) => p.url !== url),
      }))
    },
    [pages, deleteOrgDoc, token, setWizardData],
  )

  // Scrape pending pages on Next click — show progress screen during scrape
  const beforeNext = useCallback(async () => {
    if (pagesChangedRef.current && maxStepReached >= STEPS.PROFILE_GENERATING) {
      setStaleFrom((prev) =>
        prev === null
          ? STEPS.PROFILE_GENERATING
          : Math.min(prev, STEPS.PROFILE_GENERATING),
      )
    }
    const pendingUrls = pages
      .filter((p) => p.status === 'pending')
      .map((p) => p.url)
    if (pendingUrls.length === 0) return

    scrapingUrlsRef.current = pendingUrls
    setShowProgress(true)
    setHideNav(true)

    let updatedPages = [...pages]
    const localSetPages = (
      updater: PageEntry[] | ((prev: PageEntry[]) => PageEntry[]),
    ) => {
      if (typeof updater === 'function') {
        updatedPages = updater(updatedPages)
      } else {
        updatedPages = updater
      }
      setWizardData({ pages: updatedPages })
    }

    // Pass existing doc URL→ID map so batch scrape can refetch duplicates.
    // Key by scheme-stripped URL so http/https variations match.
    const stripScheme = (u: string) => u.replace(/^https?:\/\//, '')
    const existingDocsByUrl = new Map(
      websiteDocs.map((d) => [stripScheme(d.website_url!), d.id]),
    )
    const allOk = await batchScrape.run(
      pendingUrls,
      localSetPages,
      existingDocsByUrl,
    )

    // If component unmounted during scrape (exit/logout), don't advance
    if (!mountedRef.current) return false

    setShowProgress(false)
    setHideNav(false)

    // Hold the user here only for a failure we are sure of. A page still
    // 'processing' means we lost the watch, not that anything went wrong — the
    // job is running and the adoption lookup will finish it off. Blocking on
    // that pinned users to a screen of failures that were about to succeed,
    // with no way forward.
    const hasRealFailure = updatedPages.some((p) => p.status === 'error')
    if (!allOk && hasRealFailure) return false

    // A run that stops early leaves later chunks never submitted, still
    // 'pending'. Advancing on that is silent data loss from the user's side —
    // those pages are simply never read. Say so once, then let them through on
    // the next press rather than trapping them here, mirroring how the
    // duplicate-name warning on the details step is acknowledged.
    const neverSubmitted = updatedPages.filter(
      (p) => p.status === 'pending',
    ).length
    if (neverSubmitted > 0 && !partialRunAcknowledgedRef.current) {
      partialRunAcknowledgedRef.current = true
      setError(
        `We didn't get to ${neverSubmitted} of your pages. Press Next again to carry on without them, or stay here to try again.`,
      )
      return false
    }
  }, [
    pages,
    batchScrape,
    setWizardData,
    maxStepReached,
    setStaleFrom,
    setHideNav,
  ])

  useStep({
    isValid: !batchScrape.isRunning,
    beforeNext,
  })

  useEffect(() => {
    return () => {
      mountedRef.current = false
      batchScrape.abort()
      discoveryAbortedRef.current = true
    }
  }, [])

  // Progress percentage based on individual URL statuses.
  // Each URL contributes equally: pending=0%, processing=~halfway, done/error=100%.
  // We split each URL's journey into two halves so processing shows meaningful
  // intermediate values (e.g. 2 of 5 URLs processing = 20%, not jumping to 0 or 100).
  const scrapeProgress = useMemo(() => {
    const scraping = scrapingUrlsRef.current
    if (scraping.length === 0) return 0
    const perUrl = 100 / scraping.length
    let progress = 0
    for (const url of scraping) {
      const page = pages.find((p) => p.url === url)
      if (!page) continue
      if (page.status === 'ingested' || page.status === 'error')
        progress += perUrl
      else if (page.status === 'processing') progress += perUrl * 0.4
    }
    return Math.min(Math.round(progress), 99)
  }, [pages])

  // ── Animated progress: tick 1%/sec toward real scrapeProgress ────
  const [displayedProgress, setDisplayedProgress] = useState(0)
  const targetProgressRef = useRef(0)
  targetProgressRef.current = scrapeProgress

  useEffect(() => {
    if (!showProgress) {
      setDisplayedProgress(0)
      return
    }

    const timer = setInterval(() => {
      setDisplayedProgress((prev) => {
        if (prev >= targetProgressRef.current) return prev
        return prev + 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [showProgress])

  const hasDiscoveredPages = wizardData.discoveredPages.length > 1

  // ── Init: waiting for DB docs to load ─────────────────────────
  if (phase === 'init') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: COLORS.primary }} />
      </Box>
    )
  }

  // ── Discovery progress screen ─────────────────────────────────
  if (phase === 'discovering') {
    return (
      <ProgressScreen
        statusText="Finding pages on your website..."
        helpTexts={[
          "We're scanning your website to find relevant pages. This usually takes a few seconds.",
        ]}
        progress={discoveryProgress}
      />
    )
  }

  // ── Scraping progress screen ──────────────────────────────────
  if (showProgress) {
    const progressItems: ProgressItem[] = scrapingUrlsRef.current.map((url) => {
      const page = pages.find((p) => p.url === url)
      if (!page) return { name: url, status: 'pending' as const }
      return {
        name: url,
        status:
          page.status === 'ingested'
            ? ('done' as const)
            : page.status === 'processing'
              ? ('processing' as const)
              : page.status === 'error'
                ? ('error' as const)
                : ('pending' as const),
        error:
          page.status === 'error' ? formatScrapeError(page.error) : undefined,
      }
    })

    return (
      <UploadProgressScreen
        title="Scanning your webpages"
        subtitle="This may take a moment."
        items={progressItems}
        overallProgress={displayedProgress}
      />
    )
  }

  // ── Input view ───────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" sx={stepHeadingSx}>
        Your organisation's webpages
      </Typography>
      <Typography variant="body1" sx={stepDescriptionSx}>
        {hasDiscoveredPages
          ? "We've found pages from your website. Review the list below — remove any that aren't relevant and add any we missed."
          : 'Add webpages from your website that you would like us to review.'}
      </Typography>
      {!hasDiscoveredPages && pages.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            ...warningBoxSx,
            mb: 2,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              color: COLORS.warningIcon,
              fontSize: 22,
              mt: 0.25,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: COLORS.labelText, lineHeight: 1.6 }}
          >
            Don&apos;t have a website? No worries — you can skip this step.
          </Typography>
        </Box>
      )}

      {/* Page list */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: pages.length >= MAX_URLS ? COLORS.error : COLORS.muted,
            mb: 1,
            display: 'block',
          }}
        >
          {pages.length} / {MAX_URLS} URLs
        </Typography>

        {ownPages.length > 0 && (
          <>
            {externalPages.length > 0 && (
              <Typography
                variant="subtitle2"
                sx={{ color: COLORS.primary, fontWeight: 600, mt: 1, mb: 0.5 }}
              >
                Your website pages
              </Typography>
            )}
            <List disablePadding>
              {ownPages.map((page) => (
                <PageItem
                  key={page.url}
                  page={page}
                  isMainSite={page.url === mainWebsite}
                  onRemove={handleRemove}
                />
              ))}
            </List>
          </>
        )}

        {externalPages.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              sx={{ color: COLORS.primary, fontWeight: 600, mt: 2, mb: 0.5 }}
            >
              External pages
            </Typography>
            <List disablePadding>
              {externalPages.map((page) => (
                <PageItem
                  key={page.url}
                  page={page}
                  isMainSite={false}
                  onRemove={handleRemove}
                />
              ))}
            </List>
          </>
        )}

        {pages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: COLORS.secondary }}>
            <Typography variant="body2">
              No webpages added yet. Add URLs below to include website content
              in your profile.
            </Typography>
          </Box>
        )}
      </Box>

      {/* URL input */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mt: 2,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Add a URL (e.g. https://example.org/about)"
          value={urlInput}
          onChange={(e) => {
            setUrlInput(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddUrl()
          }}
          disabled={pages.length >= MAX_URLS}
          variant="outlined"
          sx={{ ...textFieldSx, flexGrow: 1, minWidth: 0 }}
          slotProps={{ input: { sx: inputSx } }}
        />
        <Button
          disableRipple
          variant="outlined"
          startIcon={<AddLinkIcon />}
          onClick={handleAddUrl}
          disabled={!urlInput.trim() || pages.length >= MAX_URLS}
          sx={{ ...outlinedButtonSx, minWidth: 'auto', flexShrink: 0 }}
        >
          Upload
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!hasDiscoveredPages && pages.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: COLORS.muted, mt: 2, textAlign: 'center' }}
        >
          No webpages to add? Click Continue to skip this step.
        </Typography>
      )}
    </Box>
  )
}

// ── Page list item ──────────────────────────────────────────

function PageItem({
  page,
  isMainSite,
  onRemove,
}: {
  page: PageEntry
  isMainSite: boolean
  onRemove: (url: string) => void
}) {
  const secondaryText = isMainSite
    ? 'Main website'
    : page.status === 'error'
      ? formatScrapeError(page.error)
      : page.status === 'ingested'
        ? page.dateAdded
          ? `Scanned on ${page.dateAdded}`
          : 'Scanned'
        : 'Ready to scan'

  return (
    <ListItem
      secondaryAction={
        page.status !== 'processing' ? (
          <IconButton
            edge="end"
            onClick={() => onRemove(page.url)}
            aria-label={`Remove ${page.url}`}
            size="small"
          >
            <DeleteOutlineIcon />
          </IconButton>
        ) : undefined
      }
      sx={{ borderBottom: `1px solid ${COLORS.border}`, py: 1 }}
    >
      <Box
        sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}
        aria-hidden="true"
      >
        <StatusIcon status={page.status} />
      </Box>
      <ListItemText
        // Lead with the page's own title where discovery found one: a bare URL
        // like /accounts/meta_verified gives the user nothing to judge by.
        primary={page.title || page.url}
        secondary={
          <>
            {page.title && (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {page.url}
              </Box>
            )}
            {/* The reason wraps onto its own line. Ellipsising it behind the URL
                hid exactly the messages the user needs — a blocked host is two
                sentences, and at dialog width none of it survived. */}
            <Box component="span" sx={{ display: 'block' }}>
              {secondaryText}
            </Box>
          </>
        }
        primaryTypographyProps={{
          variant: 'body2',
          sx: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
        secondaryTypographyProps={{ component: 'div' }}
      />
    </ListItem>
  )
}

function StatusIcon({ status }: { status: PageEntry['status'] }) {
  switch (status) {
    case 'ingested':
      return (
        <CheckCircleOutlineIcon sx={{ color: COLORS.success, fontSize: 20 }} />
      )
    case 'processing':
      return <CircularProgress size={18} sx={{ color: COLORS.primary }} />
    case 'error':
      return <ErrorOutlineIcon sx={{ color: COLORS.error, fontSize: 20 }} />
    default:
      return (
        <ScheduleOutlinedIcon
          sx={{
            color: COLORS.lightGray,
            fontSize: 18,
            opacity: 0.75,
          }}
        />
      )
  }
}
